import { withAffiliate } from './affiliate';
import {
  describeError,
  engineParams,
  formatMods,
  type ModError,
  parseMods,
} from './bang';
import { pruneInapplicable } from './conditions';
import { engineById, findEngine } from './engines';
import { applyGlobals } from './globals';
import { bangString, splitQuery } from './query';
import {
  type Engine,
  type Group,
  MissingSettingError,
  NeedsInputError,
  type Preset,
  type SettingKey,
  type Settings,
  type Values,
} from './types';

export interface Context {
  settings: Settings;
  presets: Preset[];
  groups?: Group[];
}

/** One site in a group search. */
export interface GroupMember {
  /** The member's bang as it runs, without `!`: `eb.us`. */
  bang: string;
  result:
    | {
        ok: true;
        engine: Engine;
        preset?: Preset;
        values: Values;
        terms: string;
      }
    | { ok: false; message: string };
}

/** Most sites one group opens at once. */
export const GROUP_MAX = 12;

export type Interpretation =
  | {
      kind: 'engine';
      engine: Engine;
      preset?: Preset;
      values: Values;
      errors: ModError[];
      /** The search words, plus any the preset adds. */
      terms: string;
      /** True when no bang was typed and the default was used. */
      usedDefault: boolean;
      /** The engine's saved defaults that this search picked up. */
      defaults: string[];
    }
  | { kind: 'external'; name: string; mods: string[]; terms: string }
  /** A preset whose engine is gone (a deleted custom engine). */
  | { kind: 'orphan'; preset: Preset; terms: string }
  | {
      kind: 'group';
      /** A saved group; absent for one written inline (`!eb,merc`). */
      group?: Group;
      members: GroupMember[];
      terms: string;
      /** Modifiers typed after a group's name that fit none of its sites. */
      unused: string[];
    }
  | { kind: 'empty' };

/** Why a preset whose engine is gone can't search. */
export function orphanMessage(preset: Preset): string {
  const engine = preset.engine.replace(/^custom:/, '');
  return `Preset !${preset.name} uses !${engine}, an engine that’s no longer here. Edit the preset or add the engine back.`;
}

/** A preset's bang and its aliases. */
function presetNames(preset: Preset): string[] {
  return [preset.name, ...(preset.aliases ?? [])];
}

/** Every bang your presets answer to. */
export function allPresetNames(presets: Preset[]): string[] {
  return presets.flatMap(presetNames);
}

export function findPreset(
  name: string,
  presets: Preset[]
): Preset | undefined {
  return presets.find(p => presetNames(p).some(n => n.toLowerCase() === name));
}

function findGroup(name: string, groups: Group[] = []): Group | undefined {
  return groups.find(g => g.name.toLowerCase() === name);
}

/** Works out which engine (or preset) a raw query targets and with which filters. */
export function interpret(raw: string, ctx: Context): Interpretation {
  const { bang, terms } = splitQuery(raw);

  if (!bang) {
    if (!terms) return { kind: 'empty' };
    const fallback = splitQuery(`!${ctx.settings.defaultBang || 'g'}`).bang;
    const result =
      fallback && withLooseMods(fallback.name, fallback.mods, terms, ctx);
    if (result?.kind === 'engine' && result.errors.length === 0)
      return { ...result, usedDefault: true };
    // A broken default bang shouldn't block searching.
    return interpretBang('g', [], terms, ctx, true) as Interpretation;
  }
  // `!eb.us,merc,cl` runs the search on each; a saved group does the same.
  if (bang.raw.includes(','))
    return interpretGroup(bang.raw.slice(1).split(','), [], terms, ctx);
  const group = findPreset(bang.name, ctx.presets)
    ? undefined
    : findGroup(bang.name, ctx.groups);
  if (group) return interpretGroup(group.members, bang.mods, terms, ctx, group);
  return interpretBang(bang.name, bang.mods, terms, ctx);
}

/**
 * The default engine with any `.mod` words typed without a bang
 * (`.img .d cats`) applied as its modifiers. A word that isn't one of its
 * modifiers (`.net`) stays a search word.
 */
function withLooseMods(
  name: string,
  mods: string[],
  terms: string,
  ctx: Context
): Interpretation {
  const words = terms.split(' ');
  let used = mods;
  const rest: string[] = [];
  for (const word of words) {
    const extra = /^\.[^.]/.test(word)
      ? word.slice(1).toLowerCase().split('.').filter(Boolean)
      : [];
    const r = extra.length && interpretBang(name, [...used, ...extra], '', ctx);
    if (r && r.kind === 'engine' && !r.errors.length)
      used = [...used, ...extra];
    else rest.push(word);
  }
  return interpretBang(name, used, rest.join(' '), ctx);
}

/**
 * Each member of a group, interpreted with the same words. Modifiers typed
 * after a saved group's name (`!shop.$50-200`) go to every site that has
 * them; ones no site has are reported as unused.
 */
function interpretGroup(
  written: string[],
  extra: string[],
  terms: string,
  ctx: Context,
  group?: Group
): Interpretation {
  const used = new Set<string>();
  const members = written
    .map(m => m.trim().replace(/^!/, ''))
    .filter(Boolean)
    .slice(0, GROUP_MAX)
    .map((m): GroupMember => {
      const split = splitQuery(`!${m}`).bang;
      const name = split?.name ?? m;
      const fail = (message: string): GroupMember => ({
        bang: m,
        result: { ok: false, message },
      });
      if (
        !split ||
        (findGroup(name, ctx.groups) && !findPreset(name, ctx.presets))
      )
        return fail(`!${name} is a group; groups can’t contain groups.`);
      const ok = (mods: string[]) => {
        const r = interpretBang(name, mods, terms, ctx);
        return r.kind === 'engine' && !r.errors.length ? r : undefined;
      };
      const base = interpretBang(name, split.mods, terms, ctx);
      if (base.kind === 'orphan') return fail(orphanMessage(base.preset));
      if (base.kind !== 'engine')
        return fail(`No engine or preset called !${name}.`);
      const [error] = base.errors;
      if (error) return fail(describeError(error));
      let mods = split.mods;
      for (const token of extra) {
        if (!ok([...mods, token])) continue;
        mods = [...mods, token];
        used.add(token);
      }
      const r = ok(mods) ?? base;
      return {
        bang: [name, ...mods].join('.'),
        result: {
          ok: true,
          engine: r.engine,
          preset: r.preset,
          values: r.values,
          terms: r.terms,
        },
      };
    });
  return {
    kind: 'group',
    group,
    members,
    terms,
    unused: extra.filter(t => !used.has(t)),
  };
}

/** Typed after an engine's bang (`!eb.plain`) to skip its saved defaults. */
export const PLAIN = 'plain';

/** Whether the engine has a modifier spelled `token` of its own. */
function hasToken(engine: Engine, token: string): boolean {
  return parseMods(engine, [token]).errors.length === 0;
}

/** An engine's saved default values; tokens that no longer parse are ignored. */
export function engineDefaults(engine: Engine, settings: Settings): Values {
  const raw = settings.engineDefaults?.[engine.id];
  if (!raw) return {};
  return parseMods(engine, raw.split('.').filter(Boolean)).values;
}

/**
 * Typed values plus the defaults that still fit alongside them. A default
 * gives way when it clashes with what was typed (a video length default
 * after typing .img), rather than turning the typed modifier into an error.
 */
function withDefaults(engine: Engine, typed: Values, defaults: Values): Values {
  const params = engineParams(engine);
  let values = { ...typed };
  for (const [id, v] of Object.entries(defaults)) {
    if (id in values) continue;
    const trial = { ...values, [id]: v };
    if (!pruneInapplicable(params, trial).removed.length) values = trial;
  }
  return values;
}

function interpretBang(
  name: string,
  mods: string[],
  terms: string,
  ctx: Context,
  usedDefault = false
): Interpretation {
  const preset = findPreset(name, ctx.presets);
  const engine = preset ? engineById(preset.engine) : findEngine(name);
  // Never fall through to DuckDuckGo for one of your own presets.
  if (!engine && preset) return { kind: 'orphan', preset, terms };
  if (!engine) return { kind: 'external', name, mods, terms };
  // A custom engine made before `plain` was reserved keeps its own meaning.
  const plain = mods.includes(PLAIN) && !hasToken(engine, PLAIN);
  const typed = plain ? mods.filter(m => m !== PLAIN) : mods;
  // A preset is its own set of defaults; the engine's apply to its own bangs.
  const defaults = preset || plain ? {} : engineDefaults(engine, ctx.settings);
  const alone = parseMods(engine, typed, preset?.values);
  // A typed modifier may only apply alongside a default (.open with an
  // issues default), so a failed parse is retried on top of the defaults.
  const retry =
    alone.errors.length && Object.keys(defaults).length
      ? parseMods(engine, typed, defaults)
      : undefined;
  const parsed = retry && !retry.errors.length ? retry : alone;
  const values = parsed.errors.length
    ? parsed.values
    : withDefaults(engine, parsed.values, defaults);
  const words = preset?.terms ? `${terms} ${preset.terms}`.trim() : terms;
  return {
    kind: 'engine',
    engine,
    preset,
    values,
    errors: parsed.errors,
    terms: words,
    usedDefault,
    defaults: formatMods(
      engine,
      Object.fromEntries(
        Object.entries(values).filter(
          ([id]) =>
            id in defaults &&
            !(id in alone.values) &&
            values[id] === defaults[id]
        )
      )
    ),
  };
}

export type UrlResult =
  | { ok: true; url: string }
  | { ok: false; missing: SettingKey; engine: Engine }
  | { ok: false; missing?: undefined; message: string; engine: Engine };

export function engineUrl(
  engine: Engine,
  values: Values,
  terms: string,
  settings: Settings
): UrlResult {
  try {
    const url = engine.build({
      query: applyGlobals(terms, values),
      values,
      settings,
    });
    return {
      ok: true,
      url: withAffiliate(url, settings.shoppingLinks !== false),
    };
  } catch (e) {
    if (e instanceof MissingSettingError)
      return { ok: false, missing: e.setting, engine };
    if (e instanceof NeedsInputError)
      return { ok: false, message: e.message, engine };
    // Anything else is a bug in a definition; show it rather than crash.
    return {
      ok: false,
      message: `Couldn’t build a ${engine.name} URL from this.`,
      engine,
    };
  }
}

/**
 * The canonical bang for an engine + values (always the engine's own bang).
 * With settings, `.plain` is added when the engine's saved defaults would
 * otherwise put back something these values leave out.
 */
export function canonicalBang(
  engine: Engine,
  values: Values,
  settings?: Settings
): string {
  const tokens = formatMods(engine, values);
  if (settings) {
    const restored = withDefaults(
      engine,
      values,
      engineDefaults(engine, settings)
    );
    if (formatMods(engine, restored).join('.') !== tokens.join('.'))
      tokens.push(PLAIN);
  }
  return bangString(engine.bang, tokens);
}

const REPEAT = /(^|\s)!!(?=\s|$)/;
const REPEAT_ALL = new RegExp(REPEAT.source, 'g');

/**
 * The words of the latest search that had some, for `!!`. Any other bang
 * left in them is dropped so it can't take over the new search.
 */
export function lastWords(recent: string[]): string | undefined {
  for (const q of recent) {
    const terms = splitQuery(q)
      .terms.replace(/(^|\s)![^\s!]+(?=\s|$)/g, '$1')
      .replace(/\s+/g, ' ')
      .trim();
    if (terms && !REPEAT.test(terms)) return terms;
  }
  return undefined;
}

/**
 * Replaces a standalone `!!` with the last search's words: `!cl !!` runs
 * the previous search on Craigslist. `missing` is true when there's a `!!`
 * but nothing to repeat.
 */
export function expandRepeat(
  raw: string,
  /** The words, or how to get them: only read when `!!` is there. */
  last: string | undefined | (() => string | undefined)
): { query: string; missing: boolean } {
  if (!REPEAT.test(raw)) return { query: raw, missing: false };
  const words = typeof last === 'function' ? last() : last;
  if (!words) return { query: raw.replace(REPEAT_ALL, '$1'), missing: true };
  return {
    query: raw.replace(REPEAT_ALL, (_, pre) => `${pre}${words}`),
    missing: false,
  };
}

const DDG = 'https://duckduckgo.com';

/**
 * Fills a DuckDuckGo bang template the way DuckDuckGo does. Some templates
 * are relative to duckduckgo.com, and a few put the words in the hostname;
 * those only get characters that can't change which site it is.
 */
export function ddgUrl(template: string, terms: string): string {
  // The marker is lowercase because URL() lowercases hostnames.
  let absolute: string;
  try {
    absolute = new URL(template.replace(/\{\{\{s\}\}\}/g, 'zzqzz'), DDG)
      .toString()
      .replace(/zzqzz/g, '{{{s}}}');
  } catch {
    return `${DDG}/?q=${encodeURIComponent(terms)}`;
  }
  if (!terms) {
    try {
      return new URL(absolute.replace(/\{\{\{s\}\}\}/g, '')).origin;
    } catch {
      return DDG;
    }
  }
  const at = absolute.indexOf('{{{s}}}');
  const inHost =
    at >= 0 && at < absolute.indexOf('/', absolute.indexOf('//') + 2);
  const filled = inHost
    ? terms.toLowerCase().replace(/[^a-z0-9.-]/g, '')
    : encodeURIComponent(terms).replace(/%2F/g, '/');
  return absolute.replace(/\{\{\{s\}\}\}/g, filled);
}

/** Only web URLs are ever navigated to (never javascript: or data:). */
export function isWebUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

/** Checks a bang (no `!`) on its own, without the default-engine fallback. */
export function checkBang(bang: string, presets: Preset[]): string | null {
  const split = splitQuery(`!${bang.replace(/^!/, '')}`).bang;
  if (!split) return 'Enter a bang.';
  const preset = findPreset(split.name, presets);
  const engine = preset ? engineById(preset.engine) : findEngine(split.name);
  if (!engine) return `No engine or preset called !${split.name}.`;
  const mods = hasToken(engine, PLAIN)
    ? split.mods
    : split.mods.filter(m => m !== PLAIN);
  const [first] = parseMods(engine, mods, preset?.values).errors;
  return first ? describeError(first) : null;
}
